import { LibraryTemplateInput } from '../types/LibraryTemplate';
import { getCsvDataLines, parseCsvLine, parseCsvBaseTypes } from '../Helpers/csvUtils';
import { generateSlug } from '../Helpers/slugUtils';
import { LibraryConfig } from './LibraryConfig';

/**
 * One problem found while importing a CSV row.
 * `label` is the group heading in the result modal; `detail` the row-specific text.
 */
export interface LibraryImportIssue {
    row: number;
    name: string;
    label: string;
    detail: string;
    severity: 'error' | 'warning';
}

/** One valid CSV row, ready to be written at `{library path}/{id}`. */
export interface LibraryImportRow<F extends object> {
    id: string;
    name: string;
    data: LibraryTemplateInput<F>;
}

export interface ParsedLibraryCsv<F extends object> {
    rows: LibraryImportRow<F>[];
    issues: LibraryImportIssue[];
    /** Rows that produced no template: rows with an error and rows without a name */
    skipped: number;
    /** True when any row has an error; such a file is refused as a whole */
    hasErrors: boolean;
}

/**
 * Parse a library CSV file (header "Namn;{csv.columns}[;Bastyper]") into rows.
 * The Bastyper column is present only when `withBaseTypes` is true.
 * Pure: reads nothing but its arguments, so the caller decides what to do
 * with the rows (merge into or replace the library).
 */
export function parseLibraryCsv<F extends object>(
    text: string,
    csv: LibraryConfig<F>['csv'],
    withBaseTypes = true
): ParsedLibraryCsv<F> {
    const columnCount = csv.columns.length + (withBaseTypes ? 2 : 1);
    const rows: LibraryImportRow<F>[] = [];
    const issues: LibraryImportIssue[] = [];
    const processedIds = new Set<string>();
    let skipped = 0;

    const fail = (row: number, name: string, label: string, detail: string) => {
        issues.push({ row, name, label, detail, severity: 'error' });
        skipped++;
    };

    const dataLines = getCsvDataLines(text);

    for (let i = 0; i < dataLines.length; i++) {
        const rowNum = i + 2;
        const cells = parseCsvLine(dataLines[i]).map((c) => c.trim());

        if (cells.length < columnCount) {
            fail(rowNum, cells[0] || 'Okänd', 'Ogiltigt CSV-format',
                `Ogiltigt CSV-format: förväntade ${columnCount} kolumner, hittade ${cells.length}`);
            continue;
        }

        const name = cells[0];
        if (!name) {
            skipped++;
            continue;
        }

        const parsed = csv.parseColumns(cells.slice(1, 1 + csv.columns.length));
        if (!parsed.ok) {
            fail(rowNum, name, parsed.label, parsed.detail);
            continue;
        }

        const { valid: baseTypes, invalid: invalidBaseTypes } = withBaseTypes
            ? parseCsvBaseTypes(cells[columnCount - 1])
            : { valid: [], invalid: [] };
        if (invalidBaseTypes.length > 0) {
            issues.push({
                row: rowNum,
                name,
                label: 'Ogiltiga bastyper (ignorerades)',
                detail: `Ogiltiga bastyper ignorerades: ${invalidBaseTypes.join(', ')}`,
                severity: 'warning',
            });
        }

        const id = generateSlug(name);
        if (!id) {
            fail(rowNum, name, 'Ogiltigt mall-ID',
                'Kunde inte skapa mall-ID från namn (namnet måste innehålla bokstäver eller siffror)');
            continue;
        }

        if (processedIds.has(id)) {
            fail(rowNum, name, 'Dublett-ID', `Dublett-ID: "${id}" finns redan tidigare i filen`);
            continue;
        }
        processedIds.add(id);

        rows.push({
            id,
            name,
            data: { translations: { sv: name }, baseTypes, ...parsed.fields },
        });
    }

    return { rows, issues, skipped, hasErrors: issues.some((issue) => issue.severity === 'error') };
}
